import React, { useMemo } from 'react';

/**
 * Santa Drop playfield, drawn as SVG in GM units (santa_drop_gm.py).
 * All art is drawn here in code: night sky, snow, rooftops with chimneys,
 * the sled with two reindeer, falling gifts and landing bursts.
 */
export const SD_FIELD_W = 200;
export const SD_FIELD_H = 120;
export const SD_SLED_Y = 22;
export const SD_LAND_Y = 84;
export const SD_ROOF_H = 10;
export const SD_GIFT_SIZE = 5;
export const SD_BURST_TICKS = 12; // 600 ms at the 50 ms tick
export const SD_MAX_STEPS = 1200;
export const SD_SNOW_COUNT = 46;
export const SD_STAR_COUNT = 34;

export const SD_SCENE_STYLE = {
    display: 'block',
    width: '100%',
    height: 'auto',
    aspectRatio: `${SD_FIELD_W} / ${SD_FIELD_H}`,
    borderRadius: 8,
    background: '#0b1026',
    touchAction: 'none',
    userSelect: 'none',
};

const WALL_COLORS = ['#7a3b2e', '#4f5d75', '#6b4f3a', '#3d5a4c'];
const ROOF_COLORS = ['#3b1f1a', '#2b3042', '#3a2a1f', '#22332b'];
const GIFT_COLORS = [
    ['#e63946', '#ffd166'],
    ['#2a9d8f', '#f1faee'],
    ['#8338ec', '#ffbe0b'],
    ['#06d6a0', '#ef476f'],
];

/** Small deterministic PRNG so stars/snow never flicker between renders. */
function mulberry32(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

export function makeSantaSky(seed = 7) {
    const rnd = mulberry32(seed);
    const stars = Array.from({ length: SD_STAR_COUNT }, () => ({
        x: rnd() * SD_FIELD_W,
        y: rnd() * 60,
        r: 0.25 + rnd() * 0.45,
        o: 0.35 + rnd() * 0.6,
    }));
    const snow = Array.from({ length: SD_SNOW_COUNT }, () => ({
        x: rnd() * SD_FIELD_W,
        y: rnd() * SD_FIELD_H,
        r: 0.35 + rnd() * 0.75,
        vy: 0.35 + rnd() * 0.6,
        drift: rnd() * Math.PI * 2,
    }));
    return { stars, snow };
}

/** Accepts the GM's house dicts or the compact history arrays. */
export function normalizeHouse(raw) {
    if (Array.isArray(raw)) {
        const [x, width, height, chimneyX, chimneyWidth, delivered, style = 0, claimed = 0] = raw;
        return {
            x, width, height, chimney_x: chimneyX, chimney_width: chimneyWidth,
            delivered: Boolean(delivered), style, claimed: Boolean(claimed),
        };
    }
    if (!raw || typeof raw !== 'object') return null;
    return {
        x: Number(raw.x) || 0,
        width: raw.width == null ? null : Number(raw.width),
        height: Number(raw.height) || 18,
        chimney_x: Number(raw.chimney_x) || 0,
        chimney_width: Number(raw.chimney_width) || 16,
        delivered: Boolean(raw.delivered),
        style: Number(raw.style) || 0,
        claimed: Boolean(raw.claimed),
    };
}

export function normalizeGift(raw, index = 0) {
    if (Array.isArray(raw)) return { id: index, x: raw[0], y: raw[1] };
    if (!raw || typeof raw !== 'object') return null;
    return { id: raw.id ?? index, x: Number(raw.x) || 0, y: Number(raw.y) || SD_SLED_Y };
}

function houseWidth(h) {
    if (h.width != null && Number.isFinite(h.width)) return h.width;
    return Math.max(34, h.chimney_x - h.x + h.chimney_width / 2 + 8);
}

/** GM state -> compact replay tick (same shape as santa_drop_gm.compact_tick). */
export function buildSantaDropTick(state = {}, action = 'stay') {
    const houses = (state.houses || [])
        .map(normalizeHouse)
        .filter((h) => h && h.x < SD_FIELD_W + 4)
        .map((h) => [h.x, houseWidth(h), h.height, h.chimney_x, h.chimney_width, h.delivered ? 1 : 0, h.style]);
    const gifts = (state.gifts_in_air || state.gifts || [])
        .map(normalizeGift)
        .filter(Boolean)
        .map((g) => [g.x, g.y]);
    return {
        tick: state.tick ?? state.step_count ?? 0,
        action,
        event: action,
        sled_x: state.sled_x,
        sled_pos: state.sled_pos,
        drop_lead: state.drop_lead ?? 0,
        aim_house_id: state.aim_house_id ?? null,
        score: state.score ?? 0,
        gifts_dropped: state.gifts_dropped ?? 0,
        gifts_landed: state.gifts_landed ?? 0,
        combo: state.combo ?? 0,
        houses,
        gifts,
        events: Array.isArray(state.events) ? state.events.slice() : [],
    };
}

/** Screen x of the roof point a gift dropped now lands on (GM aim_x = sled_pos + drop_lead). */
export function aimX(sledPos, dropLead) {
    return (Number(sledPos) || 0) + (Number(dropLead) || 0);
}

/**
 * Fallback only (replays without aim_house_id): index of the open chimney under
 * the marker, or -1. Mirrors santa_drop_gm.aim_house_id using the per-house
 * delivered/claimed flags the GM sends. Live play uses aim_house_id directly.
 */
export function alignedHouseIndex(houses, aim) {
    return houses.findIndex(
        (h) => h && !h.delivered && !h.claimed && Math.abs(aim - h.chimney_x) * 2 <= h.chimney_width,
    );
}

/** Gold marker? GM aim_house_id when the state carries it, else the mirrored rule. */
export function isAimAligned(aimHouseId, houses, aim) {
    if (aimHouseId !== undefined) return aimHouseId !== null;
    return alignedHouseIndex(houses, aim) >= 0;
}

/** Fraction of the run left (1 -> 0), from the GM's tick limit. */
export function timeLeftFraction(tick, maxSteps = SD_MAX_STEPS) {
    return Math.max(0, Math.min(1, 1 - (Number(tick) || 0) / maxSteps));
}

/** Last part of the run: the time bar turns red. */
export const SD_TIME_WARN_FRACTION = 1 / 6;

/** Land/miss events from recent ticks -> bursts with an age in ticks. */
export function burstsFromTicks(ticks = [], uptoIndex = ticks.length - 1) {
    const out = [];
    const from = Math.max(0, uptoIndex - SD_BURST_TICKS + 1);
    for (let i = from; i <= uptoIndex; i += 1) {
        const evs = (ticks[i] && ticks[i].events) || [];
        evs.forEach((e) => {
            if (e && (e.type === 'land' || e.type === 'miss')) {
                out.push({ type: e.type, x: e.x, y: e.y ?? SD_LAND_Y, age: uptoIndex - i });
            }
        });
    }
    return out;
}

function House({ house }) {
    const h = house;
    const w = houseWidth(h);
    const wallTop = SD_FIELD_H - h.height;
    const peakY = wallTop - SD_ROOF_H;
    const wall = WALL_COLORS[h.style % WALL_COLORS.length];
    const roof = ROOF_COLORS[h.style % ROOF_COLORS.length];
    const cw = h.chimney_width;
    const cx = h.chimney_x;
    const lit = h.delivered ? '#ffd166' : '#5c6b8a';
    const winY = wallTop + 4;
    return (
        <g data-testid="santa-house" data-delivered={h.delivered ? '1' : '0'}>
            <rect x={cx - cw / 2} y={SD_LAND_Y} width={cw} height={wallTop - SD_LAND_Y + 1} fill="#8d3b2f" />
            <rect x={cx - cw / 2 - 1} y={SD_LAND_Y - 1} width={cw + 2} height={2.4} fill={h.delivered ? '#ffd166' : '#5a2a22'} />
            <rect x={cx - cw / 2 - 1} y={SD_LAND_Y - 2} width={cw + 2} height={1.2} fill="#f1f5ff" />
            <rect x={h.x} y={wallTop} width={w} height={h.height} fill={wall} />
            <polygon points={`${h.x - 2},${wallTop} ${h.x + w / 2},${peakY} ${h.x + w + 2},${wallTop}`} fill={roof} />
            <polyline
                points={`${h.x - 2},${wallTop} ${h.x + w / 2},${peakY} ${h.x + w + 2},${wallTop}`}
                fill="none" stroke="#f1f5ff" strokeWidth={1.4} strokeLinejoin="round"
            />
            <rect x={h.x + w * 0.2} y={winY} width={5} height={5} fill={lit} opacity={0.95} />
            <rect x={h.x + w * 0.8 - 5} y={winY} width={5} height={5} fill={lit} opacity={0.95} />
            <rect x={h.x + w / 2 - 3} y={SD_FIELD_H - 9} width={6} height={9} fill="#2b1d16" />
            {h.delivered && (
                <g opacity={0.7}>
                    <circle cx={cx - 1} cy={SD_LAND_Y - 5} r={2} fill="#cfd8ea" />
                    <circle cx={cx + 2} cy={SD_LAND_Y - 9} r={2.6} fill="#cfd8ea" opacity={0.7} />
                </g>
            )}
        </g>
    );
}

function Gift({ gift }) {
    const [box, ribbon] = GIFT_COLORS[Math.abs(gift.id) % GIFT_COLORS.length];
    const s = SD_GIFT_SIZE;
    return (
        <g data-testid="santa-gift" transform={`translate(${gift.x - s / 2}, ${gift.y - s / 2})`}>
            <rect width={s} height={s} rx={0.6} fill={box} />
            <rect x={s / 2 - 0.5} width={1} height={s} fill={ribbon} />
            <rect y={s / 2 - 0.5} width={s} height={1} fill={ribbon} />
            <circle cx={s / 2 - 0.9} cy={-0.3} r={0.9} fill={ribbon} />
            <circle cx={s / 2 + 0.9} cy={-0.3} r={0.9} fill={ribbon} />
        </g>
    );
}

function Burst({ burst }) {
    const t = Math.min(1, burst.age / SD_BURST_TICKS);
    const land = burst.type === 'land';
    const r = land ? 4 + t * 20 : 2 + t * 8;
    const color = land ? '#ffd166' : '#9fb3d9';
    const rays = land ? 10 : 6;
    return (
        <g data-testid={land ? 'santa-burst-land' : 'santa-burst-miss'} opacity={1 - t * 0.85}>
            {land && <circle cx={burst.x} cy={burst.y} r={r * 0.8} fill="none" stroke="#fff3c4" strokeWidth={0.8} />}
            {Array.from({ length: rays }, (_, i) => {
                const a = (i / rays) * Math.PI * 2;
                return (
                    <line
                        key={i}
                        x1={burst.x + Math.cos(a) * r * 0.35}
                        y1={burst.y + Math.sin(a) * r * 0.35}
                        x2={burst.x + Math.cos(a) * r}
                        y2={burst.y + Math.sin(a) * r}
                        stroke={color}
                        strokeWidth={land ? 1.6 : 0.8}
                        strokeLinecap="round"
                    />
                );
            })}
            {land && (
                <text
                    x={burst.x}
                    y={burst.y - 8 - t * 14}
                    fontSize={9}
                    textAnchor="middle"
                    fill="#ffd166"
                    stroke="#3b1f1a"
                    strokeWidth={0.4}
                    fontWeight="bold"
                >
                    +50
                </text>
            )}
            {!land && <text x={burst.x} y={burst.y - 6 - t * 6} fontSize={5} textAnchor="middle" fill="#9fb3d9">miss</text>}
        </g>
    );
}

function AimMarker({ x, aligned, scale = 1 }) {
    const color = aligned ? '#ffd166' : '#ffffff';
    return (
        <g
            data-testid="santa-aim-marker"
            data-aligned={aligned ? '1' : '0'}
            data-x={x}
            data-scale={scale}
            transform={`translate(${x}, ${SD_LAND_Y}) scale(${scale})`}
            opacity={aligned ? 1 : 0.75}
        >
            <line x1={-6} y1={0} x2={6} y2={0} stroke={color} strokeWidth={aligned ? 1.2 : 0.8} strokeDasharray={aligned ? undefined : '1.5 1'} />
            <path d="M-3,-7 L0,-2.5 L3,-7" fill="none" stroke={color} strokeWidth={aligned ? 1.4 : 1} strokeLinecap="round" strokeLinejoin="round" />
            {aligned && <circle cx={0} cy={0} r={4.5} fill="none" stroke={color} strokeWidth={0.6} opacity={0.8} />}
        </g>
    );
}

function TimeBar({ tick, maxSteps }) {
    const frac = timeLeftFraction(tick, maxSteps);
    const warn = frac <= SD_TIME_WARN_FRACTION;
    const color = warn ? '#ef476f' : '#7ee0c3';
    return (
        <g data-testid="santa-timer" data-warn={warn ? '1' : '0'} aria-label="Time left">
            <rect x={0} y={0} width={SD_FIELD_W} height={2.4} fill="#ffffff" opacity={0.12} />
            <rect data-testid="santa-timer-bar" x={0} y={0} width={SD_FIELD_W * frac} height={2.4} fill={color} />
        </g>
    );
}

function Reindeer({ x, y, phase }) {
    const legSwing = Math.sin(phase) * 1.2;
    return (
        <g transform={`translate(${x}, ${y})`}>
            <ellipse cx={0} cy={0} rx={4.2} ry={2} fill="#8b5a2b" />
            <rect x={2.6} y={-3.4} width={1.4} height={3} fill="#8b5a2b" />
            <ellipse cx={4.4} cy={-3.8} rx={1.9} ry={1.3} fill="#8b5a2b" />
            <circle cx={6.1} cy={-3.7} r={0.6} fill="#e63946" />
            <path d="M3.6,-4.8 L3,-7 M3,-6.2 L2,-7 M4.6,-4.9 L5.2,-7.1 M5.1,-6.3 L6.1,-6.9" stroke="#d9b38c" strokeWidth={0.45} fill="none" />
            <line x1={-3} y1={1.4} x2={-3 - legSwing} y2={4} stroke="#6b4423" strokeWidth={0.7} />
            <line x1={-1.4} y1={1.6} x2={-1.4 + legSwing} y2={4} stroke="#6b4423" strokeWidth={0.7} />
            <line x1={2} y1={1.4} x2={2 + legSwing} y2={4} stroke="#6b4423" strokeWidth={0.7} />
            <line x1={3.2} y1={1.3} x2={3.2 - legSwing} y2={4} stroke="#6b4423" strokeWidth={0.7} />
        </g>
    );
}

function Sled({ x, tick }) {
    const bob = Math.sin(tick / 4) * 0.8;
    const y = SD_SLED_Y + bob;
    return (
        <g data-testid="santa-sled" transform={`translate(${x}, ${y})`}>
            <path d="M8,-5 L11,-6.5 M10,-4.8 L19,-6.5" stroke="#d4a373" strokeWidth={0.4} fill="none" />
            <Reindeer x={13} y={-7} phase={tick / 2} />
            <Reindeer x={22} y={-8} phase={tick / 2 + 1.4} />
            <path d="M-9,-1 L9,-1 Q11,-1 11,-3 L11,-9 L7,-9 L7,-5 L-7,-5 Q-9,-5 -9,-3 Z" fill="#c1121f" />
            <path d="M-10,0.6 L10,0.6 Q13,0.6 13,-2.4" stroke="#ffd166" strokeWidth={0.9} fill="none" strokeLinecap="round" />
            <rect x={-8} y={-11} width={6} height={6} rx={1} fill="#2a9d8f" />
            <rect x={-5.5} y={-11} width={1} height={6} fill="#ffd166" />
            <circle cx={2} cy={-11} r={3.2} fill="#d62828" />
            <circle cx={2.6} cy={-12.8} r={1.6} fill="#f4c7a1" />
            <path d="M0.9,-12.2 Q2.6,-9.4 4.3,-12.2 Z" fill="#ffffff" />
            <path d="M0.6,-13.6 L2.6,-17 L4.6,-13.6 Z" fill="#d62828" />
            <circle cx={2.6} cy={-17.1} r={0.8} fill="#ffffff" />
            <rect x={0.4} y={-14} width={4.4} height={0.9} fill="#ffffff" />
        </g>
    );
}

/**
 * @param {object} props
 * @param {Array} props.houses   GM house dicts or compact arrays
 * @param {Array} props.gifts    gifts in the air ({x, y} or [x, y])
 * @param {number} props.sledPos  sled position in field units (GM sled_pos)
 * @param {number} props.dropLead GM drop_lead: scroll distance during a gift's fall
 * @param {boolean} props.showAim draw the landing marker
 * @param {number} props.tick    drives snow / bobbing animation and the time bar
 * @param {number} props.maxSteps GM meta.max_steps (time bar); pass null to hide it
 * @param {number|null} props.aimHouseId GM aim_house_id (gold marker); omit for old replays
 * @param {number} props.markerScale  marker size multiplier (bigger on phones)
 * @param {Array} props.bursts   [{type: 'land'|'miss', x, y, age}]
 * @param {'paused'|'over'|null} props.overlay
 */
export default function SantaDropScene({
    houses = [],
    gifts = [],
    sledPos = 90,
    dropLead = 28,
    showAim = true,
    tick = 0,
    maxSteps = SD_MAX_STEPS,
    aimHouseId,
    markerScale = 1,
    bursts = [],
    overlay = null,
    overlayText,
    style,
    'data-testid': testId = 'santa-drop-scene',
}) {
    const sky = useMemo(() => makeSantaSky(), []);
    const hs = useMemo(() => houses.map(normalizeHouse).filter(Boolean), [houses]);
    const gs = useMemo(() => gifts.map(normalizeGift).filter(Boolean), [gifts]);
    const snowOffset = tick;
    const aim = aimX(sledPos, dropLead);
    const aligned = isAimAligned(aimHouseId, hs, aim);

    return (
        <svg
            viewBox={`0 0 ${SD_FIELD_W} ${SD_FIELD_H}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Santa Drop playfield"
            data-testid={testId}
            style={{ ...SD_SCENE_STYLE, ...style }}
        >
            <defs>
                <linearGradient id="sdSky" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#070b1f" />
                    <stop offset="65%" stopColor="#16204a" />
                    <stop offset="100%" stopColor="#2b3a6b" />
                </linearGradient>
                <radialGradient id="sdMoon" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#fffbe6" />
                    <stop offset="70%" stopColor="#f6e7b4" />
                    <stop offset="100%" stopColor="#f6e7b4" stopOpacity="0" />
                </radialGradient>
            </defs>
            <rect x={0} y={0} width={SD_FIELD_W} height={SD_FIELD_H} fill="url(#sdSky)" />
            {sky.stars.map((s, i) => (
                <circle key={`st${i}`} cx={s.x} cy={s.y} r={s.r} fill="#ffffff" opacity={s.o} />
            ))}
            <circle cx={30} cy={16} r={11} fill="url(#sdMoon)" />
            <circle cx={30} cy={16} r={7} fill="#fff6d5" />
            <path d="M0,104 Q30,92 60,100 T120,98 T200,96 L200,120 L0,120 Z" fill="#1d2a52" opacity={0.85} />

            {hs.map((h, i) => <House key={`h${i}-${h.chimney_x}`} house={h} />)}
            <rect x={0} y={SD_FIELD_H - 3} width={SD_FIELD_W} height={3} fill="#e8eefc" />

            {showAim && <AimMarker x={aim} aligned={aligned} scale={markerScale} />}
            {gs.map((g) => <Gift key={`g${g.id}`} gift={g} />)}
            {bursts.map((b, i) => <Burst key={`b${i}-${b.x}-${b.age}`} burst={b} />)}
            <Sled x={sledPos} tick={tick} />

            {sky.snow.map((f, i) => {
                const y = (f.y + snowOffset * f.vy) % SD_FIELD_H;
                const x = (f.x + Math.sin(f.drift + snowOffset / 12) * 3 + SD_FIELD_W) % SD_FIELD_W;
                return <circle key={`sn${i}`} cx={x} cy={y} r={f.r} fill="#ffffff" opacity={0.8} />;
            })}

            {maxSteps ? <TimeBar tick={tick} maxSteps={maxSteps} /> : null}

            {overlay && (
                <g data-testid={`santa-overlay-${overlay}`}>
                    <rect x={0} y={0} width={SD_FIELD_W} height={SD_FIELD_H} fill="#000" opacity={0.55} />
                    <text x={SD_FIELD_W / 2} y={SD_FIELD_H / 2} fontSize={11} fontWeight="bold" textAnchor="middle" fill="#ffffff">
                        {overlayText || (overlay === 'paused' ? 'Paused' : 'Run complete')}
                    </text>
                </g>
            )}
        </svg>
    );
}
